import { G } from '@mobily/ts-belt';

type Nullish<T> = T | null | undefined;

type CorrespondenceAddress = {
  is_correspondent: boolean;
  street: Nullish<string>;
  postalcode: Nullish<string>;
  city: Nullish<string>;
  country: Nullish<string>;
  is_npai: boolean;
  npai_at: Nullish<string>;
};

export type B2bAdminCorrespondencePayload = {
  is_mailing_correspondent: boolean;
  mailing_street: Nullish<string>;
  mailing_postalcode: Nullish<string>;
  mailing_city: Nullish<string>;
  mailing_country: Nullish<string>;
  is_npai_mailing: boolean;
  npai_mailing_at: Nullish<string>;
  is_billing_correspondent: boolean;
  billing_channel: Nullish<'paper' | 'email' | 'both'>;
  billing_street: Nullish<string>;
  billing_postalcode: Nullish<string>;
  billing_city: Nullish<string>;
  billing_country: Nullish<string>;
  is_npai_billing: boolean;
  npai_billing_at: Nullish<string>;
};

export function isBillingChannelWithPaper(channel: Nullish<string>): boolean {
  return channel === 'paper' || channel === 'both';
}

function countFilled(...values: Nullish<string>[]): number {
  return values.filter((value) => G.isNotNullable(value) && value !== '').length;
}

export function hasCorrespondenceAddress(
  address: Pick<CorrespondenceAddress, 'street' | 'postalcode' | 'city' | 'country'>,
): boolean {
  return countFilled(address.street, address.postalcode, address.city, address.country) > 0;
}

function validateAddress(kind: 'mailing' | 'billing', address: CorrespondenceAddress) {
  const filled = countFilled(address.street, address.postalcode, address.city, address.country);

  if (filled !== 0 && filled !== 4) {
    return `The ${kind} address must be fully filled or fully empty`;
  }
  if (filled === 4 && !address.is_correspondent) {
    return `A ${kind} address can only be set on a ${kind} correspondent`;
  }
  if (address.is_npai && filled === 0) {
    return `The ${kind} address cannot be PND without an address`;
  }
  if (address.is_npai !== G.isNotNullable(address.npai_at)) {
    return `The ${kind} PND date must be set if and only if the ${kind} address is PND`;
  }
  return null;
}

/**
 * Cross-field rules of the correspondent mailing/billing addresses, shared by the backend and the BO forms.
 * Returns the first error message, or `null` when the payload is valid.
 */
export function validateAdminCorrespondence(payload: B2bAdminCorrespondencePayload): string | null {
  const mailingError = validateAddress('mailing', {
    is_correspondent: payload.is_mailing_correspondent,
    street: payload.mailing_street,
    postalcode: payload.mailing_postalcode,
    city: payload.mailing_city,
    country: payload.mailing_country,
    is_npai: payload.is_npai_mailing,
    npai_at: payload.npai_mailing_at,
  });
  if (mailingError !== null) {
    return mailingError;
  }

  const billingError = validateAddress('billing', {
    is_correspondent: payload.is_billing_correspondent,
    street: payload.billing_street,
    postalcode: payload.billing_postalcode,
    city: payload.billing_city,
    country: payload.billing_country,
    is_npai: payload.is_npai_billing,
    npai_at: payload.npai_billing_at,
  });
  if (billingError !== null) {
    return billingError;
  }

  if (payload.is_billing_correspondent !== G.isNotNullable(payload.billing_channel)) {
    return 'The invoicing channel must be set if and only if the admin is a billing correspondent';
  }
  if (
    isBillingChannelWithPaper(payload.billing_channel) &&
    !hasCorrespondenceAddress({
      street: payload.billing_street,
      postalcode: payload.billing_postalcode,
      city: payload.billing_city,
      country: payload.billing_country,
    })
  ) {
    return 'A billing address is required when invoices are sent by paper';
  }
  return null;
}
